import { v4 as uuid } from "uuid";
import { PRODUCT_NAME } from "../constants/ApiEndpointConstants";
import LocalStorageUtil from "../utils/LocalStorageUtil";

const EVENT_ID_WINDOWS = Object.freeze({
  ViewContent: 24 * 60 * 60 * 1000,
  CustomizeProduct: 60 * 60 * 1000,
});

const generateRandomString = (length) => {
  const value = uuid();
  return length ? value.substring(0, length) : value;
};

const generateRandomEmail = (romajiName) => {
  if (!romajiName) return romajiName;

  const normalizedName = romajiName.toLowerCase().replaceAll(" ", "");
  const randomEmailSuffix = [
    "gmail.com",
    "yahoo.com",
    "icloud.com",
    "yahoo.co.jp",
    "ezweb.ne.jp",
    "ybb.ne.jp",
    "yahoo.ne.jp",
    "nmt.ne.jp",
    "ymail.ne.jp",
  ];
  const suffix =
    randomEmailSuffix[Math.floor(Math.random() * randomEmailSuffix.length)];

  return `${normalizedName}${generateRandomString(6)}@${suffix}`;
};

const normalizeEventIdPart = (value, fallback) => {
  const normalized = String(value ?? "")
    .trim()
    .replace(/[^a-zA-Z0-9._-]+/g, "_")
    .replace(/^_+|_+$/g, "")
    .slice(0, 48);

  return normalized || fallback;
};

const getAnonymousUserId = () => {
  const storedId = LocalStorageUtil.loadAnonymousUserId();
  if (storedId) return normalizeEventIdPart(storedId, "anonymous");

  const generatedId = generateRandomString(16);
  try {
    LocalStorageUtil.saveAnonymousUserId(generatedId);
  } catch {
    // Private browsing can reject storage writes; this event still gets a valid ID.
  }
  return generatedId;
};

const getWindowTimestamp = (eventName, now) => {
  const windowDuration = EVENT_ID_WINDOWS[eventName];
  if (!windowDuration) return null;

  const storedTimestamp = Number(LocalStorageUtil.loadPageVisited(eventName));
  if (
    Number.isFinite(storedTimestamp) &&
    storedTimestamp > 0 &&
    now - storedTimestamp < windowDuration
  ) {
    return storedTimestamp;
  }

  try {
    LocalStorageUtil.savePageVisited(eventName);
    return Number(LocalStorageUtil.loadPageVisited(eventName)) || now;
  } catch {
    return now;
  }
};

const generateEventId = (eventName, productId) => {
  const now = Date.now();
  const parts = [
    normalizeEventIdPart(PRODUCT_NAME, "kyc"),
    getAnonymousUserId(),
    normalizeEventIdPart(eventName, "event"),
  ];

  if (productId) {
    parts.push(normalizeEventIdPart(productId, "item"));
  }

  const windowTimestamp = getWindowTimestamp(eventName, now);
  parts.push(
    windowTimestamp
      ? windowTimestamp.toString(36)
      : `${now.toString(36)}-${generateRandomString(8)}`,
  );

  return parts.join("-");
};

const randomPrice = (max, min) =>
  Math.floor(Math.random() * (max - min + 1)) + min;

export {
  generateRandomString,
  generateRandomEmail,
  generateEventId,
  randomPrice,
};
